import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useApprovals } from './ApprovalProvider';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// The greeting at the top of the dashboard: "¡Hola, X!" and what needs doing today, each item a link to where it's done
// (GET /dashboard/brief; the approvals count is the header bell's). Shown to admins and sales alike; a sales user's cobros
// and facturas are their own.
const DailyBrief = () => {
  const { user } = useAuth() || {};
  const { summary } = useApprovals();
  const [brief, setBrief] = useState(null);
  const isAdmin = user?.role === 'admin';

  useEffect(() => { API.get('/dashboard/brief').then(r => setBrief(r.data)).catch(() => setBrief(false)); }, []);

  const name = user?.username || localStorage.getItem('userName') || '';
  const items = [];
  if (brief) {
    const { rentals, returns, cobros } = brief;
    if (rentals.dueToday || rentals.overdue) {
      items.push({
        icon: 'bi-arrow-repeat', color: 'var(--mm-cyan)', to: '/rentals?filtro=por-facturar',
        text: [rentals.dueToday && `${plural(rentals.dueToday, 'alquiler', 'alquileres')} por renovar hoy`,
          rentals.overdue && `${plural(rentals.overdue, 'atrasado', 'atrasados')} sin facturar`].filter(Boolean).join(', más '),
        late: rentals.overdue > 0,
      });
    }
    if (returns.dueToday || returns.late) {
      items.push({
        icon: 'bi-box-arrow-in-left', color: 'var(--mm-teal)', to: '/rentals?filtro=vencidos',
        text: [returns.dueToday && `${plural(returns.dueToday, 'equipo', 'equipos')} por recoger hoy`,
          returns.late && `${plural(returns.late, 'alquiler pasado', 'alquileres pasados')} de su fecha de devolución`].filter(Boolean).join(' y '),
        late: returns.late > 0,
      });
    }
    if (cobros.dueToday.count || cobros.overdue.count) {
      items.push({
        icon: 'bi-cash-coin', color: 'var(--mm-green)', to: '/ingresos',
        text: [cobros.dueToday.count && `${plural(cobros.dueToday.count, 'factura vence', 'facturas vencen')} hoy (${money(cobros.dueToday.balance)})`,
          cobros.overdue.count && `${plural(cobros.overdue.count, 'factura vencida', 'facturas vencidas')} por cobrar (${money(cobros.overdue.balance)})`].filter(Boolean).join('; '),
        late: cobros.overdue.count > 0,
      });
    }
    if (brief.readyToSend) {
      items.push({ icon: 'bi-send', color: 'var(--mm-blue)', to: '/invoices', text: `${plural(brief.readyToSend, 'factura lista', 'facturas listas')} para enviar a DGII` });
    }
    if (isAdmin && brief.toVerify) {
      items.push({ icon: 'bi-patch-check', color: 'var(--mm-yellow)', to: '/ingresos?tab=verify', text: `${plural(brief.toVerify, 'ingreso', 'ingresos')} por verificar` });
    }
    if (brief.toDeliver) {
      items.push({ icon: 'bi-truck', color: 'var(--mm-orange)', to: '/conduces', text: `${plural(brief.toDeliver, 'conduce', 'conduces')} por entregar` });
    }
  }
  if (summary?.pending) {
    items.push({
      icon: 'bi-hourglass-split', color: 'var(--mm-yellow)', to: '/aprobaciones',
      text: isAdmin ? `${plural(summary.pending, 'solicitud espera', 'solicitudes esperan')} tu aprobación` : `${plural(summary.pending, 'solicitud tuya espera', 'solicitudes tuyas esperan')} aprobación`,
    });
  }
  if (!isAdmin && summary?.denied) {
    items.push({ icon: 'bi-x-octagon', color: 'var(--mm-red)', to: '/aprobaciones', text: `${plural(summary.denied, 'solicitud tuya fue denegada', 'solicitudes tuyas fueron denegadas')}`, late: true });
  }

  return (
    <Card className="shadow-sm p-3 mb-3" style={{ borderLeft: '4px solid var(--mm-brand)' }}>
      <div style={{ fontSize: '1.35rem', fontWeight: 700 }}>¡Hola, {name}!</div>
      {brief === null && <div className="text-muted small mt-1">Revisando lo que hay para hoy…</div>}
      {brief === false && <div className="text-muted small mt-1">No se pudo cargar el resumen de hoy.</div>}
      {brief && items.length === 0 && <div className="mt-1" style={{ color: 'var(--mm-muted)' }}>Todo al día para hoy. ¡Buen trabajo!</div>}
      {brief && items.length > 0 && (
        <>
          <div className="mt-1 mb-2" style={{ color: 'var(--mm-muted)' }}>Esto es lo que tienes para hoy:</div>
          <ul className="list-unstyled mb-0 daily-brief">
            {items.map(it => (
              <li key={it.to + it.text} className="d-flex align-items-start gap-2 mb-1">
                <i className={`bi ${it.icon}`} style={{ color: it.color, marginTop: 2 }} />
                <Link to={it.to} style={{ color: it.late ? '#FCA5A5' : undefined }}>{it.text}</Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
};

export default DailyBrief;
